import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import type { MenuProps } from 'antd';
import type { UseNavbar } from '../Navbar.types';
import { ALLOWED_PATHS, CUSTOM_PACKAGE_GROUP_PATHS, FORECASTING_GROUP_PATHS } from '@/constants/paths';
import { ITEM_DATA, RATE_BUILDER_GROUP_ICON } from '../Navbar.constants';
import { PRIVATE_PATH } from '@/constants';

const CUSTOM_PACKAGE_GROUP_KEY = 'custom-package-group';
const CUSTOM_PACKAGE_GROUP_I18N_KEY = 'skyriseRateBuilder';
const FORECASTING_GROUP_KEY = 'forecasting-group';

export const useNavbar: UseNavbar = ({ role }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [current, setCurrent] = useState('/');

  const allowedPaths = useMemo(() => {
    const rawPaths = role != null ? ALLOWED_PATHS[role as keyof typeof ALLOWED_PATHS] : undefined;
    return (rawPaths ?? []).filter((path) => ITEM_DATA[path] && !ITEM_DATA[path].disabled);
  }, [role]);

  const menuItems: MenuProps['items'] = useMemo(() => {
    const items: MenuProps['items'] = [];

    for (const path of allowedPaths) {
      if (path === PRIVATE_PATH.CUSTOM_PACKAGE) {
        const children = CUSTOM_PACKAGE_GROUP_PATHS
          .filter((p) => allowedPaths.includes(p) && !ITEM_DATA[p].disabled)
          .map((p) => {
            const { i18nKey, icon, key } = ITEM_DATA[p];
            return {
              key: p,
              icon,
              label: <Link to={p}>{t(i18nKey)}</Link>
            };
          });
        if (children.length > 0) {
          items.push({
            key: CUSTOM_PACKAGE_GROUP_KEY,
            icon: RATE_BUILDER_GROUP_ICON,
            label: t(CUSTOM_PACKAGE_GROUP_I18N_KEY),
            children
          });
        }
        continue;
      }
      if (path === PRIVATE_PATH.FORECASTING) {
        const children = FORECASTING_GROUP_PATHS.filter((p) => allowedPaths.includes(p)).map((p) => {
          const { i18nKey, icon, key } = ITEM_DATA[p];
          return {
            key: p,
            icon,
            label: <Link to={p}>{t(i18nKey)}</Link>
          };
        });
        if (children.length > 0) {
          items.push({
            key: FORECASTING_GROUP_KEY,
            icon: ITEM_DATA[PRIVATE_PATH.FORECASTING].icon,
            label: t('salesDashboard'),
            children
          });
        }
        continue;
      }
      if (path === PRIVATE_PATH.FORECASTING_THREE_MONTH || path === PRIVATE_PATH.FORECASTING_MARGINS) {
        continue;
      }
      if (
        path === PRIVATE_PATH.EU6_CALCULATOR ||
        path === PRIVATE_PATH.CALCULATOR_RATE_CARD
      ) {
        continue;
      }
      if (path === PRIVATE_PATH.USER_SETTINGS) {
        continue;
      }
      // Trader controls live in the bottom section (above User settings), not in the main nav
      if (
        path === PRIVATE_PATH.TRADER_CONTROLS ||
        path === PRIVATE_PATH.TRADER_CONTROLS_CHANNELS ||
        path === PRIVATE_PATH.TRADER_CONTROLS_DSP_FEES ||
        path === PRIVATE_PATH.TRADER_CONTROLS_TRADING_DEALS ||
        path === PRIVATE_PATH.TRADER_CONTROLS_KPIS
      ) {
        continue;
      }
      const { i18nKey, icon, key } = ITEM_DATA[path];
      items.push({
        key,
        icon,
        label: <Link to={path}>{t(i18nKey)}</Link>
      });
    }

    return items;
  }, [allowedPaths, t]);

  useEffect(() => {
    if (pathname.startsWith(PRIVATE_PATH.FORECASTING)) {
      setCurrent(pathname);
      return;
    }
    setCurrent(`/${pathname.split('/')[1]}` || '/');
  }, [pathname]);

  const onClick: MenuProps['onClick'] = (e) => {
    setCurrent(e.key);
  };

  return { menuItems, current, onClick };
};
